import http from "./http";

export type SystemConfig = {
	holdingPeriodDays: number;
	defaultSystemRate: number;
	minimumPayoutUsd: number;
	maxPayoutUsd: number;
	startDate: string;
	systemTimezone: string;
	bookingValidateByStationTz: boolean;
	showEsimServiceButton: boolean;
	showAirportServiceButton: boolean;
	homepageMaintenanceEnabled: boolean;
	/** Trang chủ: chip gợi ý dưới ô tìm kiếm. */
	homepageSuggestedKeywordsEnabled: boolean;
	/** Trang chủ: block “Top Destinations” (ảnh + See all). */
	homepageTopDestinationsBlockEnabled: boolean;
	disableStationNotifications: boolean;
	/** Bật luồng PIN 4 số xác minh khách tại station (đồng bộ public config + backend). */
	orderVerificationPinEnabled: boolean;
	/**
	 * 1–31: PIN hết hạn lúc 00:00 (múi giờ hệ thống) vào ngày (ngày drop-off theo lịch + N).
	 * 0: dùng orderVerificationPinExpiryMinutes sau timeIn (legacy).
	 */
	orderVerificationPinExpiryCalendarDays: number;
	/** Khi calendar days = 0: số phút sau timeIn đến khi PIN hết hạn */
	orderVerificationPinExpiryMinutes: number;
	/** YYYY-MM-DD: chỉ đơn có thời điểm tạo (CreatedAt) từ 0:00 ngày này (múi giờ hệ thống) mới PIN/verify; để trống = mọi đơn */
	orderVerificationEffectiveFrom: string;
	/** Job cron đánh dấu No-Show khi PIN quá hạn */
	verificationExpireJobEnabled: boolean;
	/** Biểu thức cron (múi giờ Asia/Ho_Chi_Minh) */
	verificationExpireCron: string;
	/** Số ngày lịch sau ngày kết thúc booking (timeOut) thì job có thể chuyển đơn sang Finished */
	orderAutoFinishDaysAfterVerify: number;
	/** Bật cron chuyển Paid/Reserved → Finished theo orderAutoFinishDaysAfterVerify */
	summaryFinishOrderJobEnabled: boolean;
	/** Thông báo toàn cục trên Station Portal (partner/station) */
	stationPortalBroadcastEnabled: boolean;
	stationPortalBroadcastTitle: string;
	stationPortalBroadcastBody: string;
	/** ISO 8601; để trống = hiển thị ngay khi bật (vẫn cần thời điểm kết thúc) */
	stationPortalBroadcastStartsAt: string;
	/** ISO 8601; bắt buộc khi bật */
	stationPortalBroadcastEndsAt: string;
	stationPortalBroadcastDisplayMode: "popup" | "floating_button";
	/**
	 * Tuỳ chọn YYYY-MM-DD (theo calendar systemTimezone): chỉ trạm có Partner.createdAt trong ngày này hoặc trước đó mới thấy broadcast.
	 */
	stationPortalBroadcastPartnerCreatedOnOrBefore: string;
	/**
	 * Tuỳ chọn YYYY-MM-DD (theo calendar systemTimezone): trạm có Partner.createdAt trong ngày này hoặc sau đó
	 * sẽ không bị bắt accept legal agreement lại trên station portal vì đã ký ở Become a Partner.
	 */
	stationPortalLegalAgreementExemptPartnerCreatedOnOrAfter: string;
	/**
	 * YYYY-MM-DD: chỉ station có Partner.createdAt từ ngày này trở đi mới thấy Welcome Aboard onboarding form.
	 */
	stationPortalOnboardingRequiredFromDate: string;
	/**
	 * Hotline mặc định cho toàn bộ station.
	 * defaultMainHotline → slot [0], defaultGeneralHotline → slot [2], defaultStationSupportHotline → slot [3].
	 * Slot [1] không bao giờ bị đụng. Để trống = không auto-fill slot đó.
	 */
	defaultMainHotline: string;
	defaultGeneralHotline: string;
	defaultStationSupportHotline: string;
	/**
	 * Hotline hiển thị cho KHÁCH trên trang whalelo.net (Contact/FAQ/đặt hàng).
	 * Khác với 3 trường defaultXxxHotline ở trên — những trường đó chỉ là giá trị
	 * mặc định tự điền vào Station khi tạo mới, không liên quan đến trang khách.
	 */
	landingHotline: string;
	/** Lịch sử thông báo gửi từ màn Quản lý thông báo (đồng bộ từ server) */
	stationPortalBroadcastHistory?: StationPortalBroadcastHistoryItem[];
	updatedAt?: string;
};

export type StationPortalBroadcastHistoryItem = {
	id: string;
	sentAt: string;
	title: string;
	body: string;
	stationPortalBroadcastStartsAt: string;
	stationPortalBroadcastEndsAt: string;
	displayMode: "popup" | "floating_button";
	status?: "draft" | "published";
	publishedAt?: string;
};

export type PaymentCycleConfig = {
	cycle1EndDay: number;
	cycle2EmailDay: number;
	cycle1PayStart: number;
	cycle1PayEnd: number;
	cycle2PayStart: number;
	cycle2PayEnd: number;
	updatedAt?: string;
};

const DEFAULTS: SystemConfig = {
	holdingPeriodDays: 11,
	defaultSystemRate: 2.25,
	minimumPayoutUsd: 0,
	maxPayoutUsd: 0,
	startDate: "2026-01-01",
	systemTimezone: "Asia/Ho_Chi_Minh",
	bookingValidateByStationTz: true,
	showEsimServiceButton: true,
	showAirportServiceButton: true,
	homepageMaintenanceEnabled: false,
	homepageSuggestedKeywordsEnabled: true,
	homepageTopDestinationsBlockEnabled: true,
	disableStationNotifications: false,
	orderVerificationPinEnabled: false,
	orderVerificationPinExpiryCalendarDays: 3,
	orderVerificationPinExpiryMinutes: 24 * 60,
	orderVerificationEffectiveFrom: "",
	verificationExpireJobEnabled: false,
	verificationExpireCron: "0 */2 * * *",
	orderAutoFinishDaysAfterVerify: 11,
	summaryFinishOrderJobEnabled: true,
	stationPortalBroadcastEnabled: false,
	stationPortalBroadcastTitle: "",
	stationPortalBroadcastBody: "",
	stationPortalBroadcastStartsAt: "",
	stationPortalBroadcastEndsAt: "",
	stationPortalBroadcastDisplayMode: "popup",
	stationPortalBroadcastPartnerCreatedOnOrBefore: "",
	stationPortalLegalAgreementExemptPartnerCreatedOnOrAfter: "",
	stationPortalOnboardingRequiredFromDate: "",
	defaultMainHotline: "",
	defaultGeneralHotline: "",
	defaultStationSupportHotline: "0866888431",
	landingHotline: "+84 866 888 431",
};

const CYCLE_DEFAULTS: PaymentCycleConfig = {
	cycle1EndDay: 15,
	cycle2EmailDay: 1,
	cycle1PayStart: 1,
	cycle1PayEnd: 5,
	cycle2PayStart: 16,
	cycle2PayEnd: 20,
};

export async function getSystemConfig(): Promise<SystemConfig> {
	const { data } = await http.get(`/utility/system-config`);
	return { ...DEFAULTS, ...(data?.data || {}) };
}

export async function saveSystemConfig(config: SystemConfig) {
	const { data } = await http.post(`/utility/system-config`, { data: config });
	return data;
}

/** Returns the count of active stations that would be overwritten by a bulk hotline apply. */
export async function getHotlineAffectedCount(): Promise<number> {
	const { data } = await http.get(`/utility/system-config/hotline-affected-count`);
	return data?.count ?? 0;
}

export async function getPaymentCycleConfig(): Promise<PaymentCycleConfig> {
	const { data } = await http.get(`/utility/payment-cycle-config`);
	return { ...CYCLE_DEFAULTS, ...(data?.data || {}) };
}

export async function savePaymentCycleConfig(config: PaymentCycleConfig) {
	const { data } = await http.post(`/utility/payment-cycle-config`, { data: config });
	return data;
}

export async function runFinishOrderJob(): Promise<{
	message: string;
	totalFinished: number;
	startedAt: string;
	finishedAt: string;
}> {
	const { data } = await http.post(`/utility/run-finish-order-job`);
	return data;
}

export async function previewFinishOrderJob(): Promise<{
	total: number;
	orders: Array<{ id: number; status: number; timeOut: string; verifiedAt: string | null }>;
}> {
	const { data } = await http.get(`/utility/preview-finish-order-job`);
	return data;
}

export async function saveBroadcastDraft(payload: {
	title: string;
	body: string;
	startsAt: string;
	endsAt: string;
	displayMode: "popup" | "floating_button";
}): Promise<StationPortalBroadcastHistoryItem[]> {
	const { data } = await http.post("/utility/system-config/broadcast-draft", payload);
	return data?.data;
}

export async function publishBroadcastDraft(historyId: string): Promise<any> {
	const { data } = await http.post(`/utility/system-config/broadcast-publish/${historyId}`);
	return data?.data;
}

export async function unpublishBroadcast(historyId: string): Promise<StationPortalBroadcastHistoryItem[]> {
	const { data } = await http.post(`/utility/system-config/broadcast-unpublish/${historyId}`);
	return data?.data;
}

export async function deleteBroadcastHistory(historyId: string): Promise<StationPortalBroadcastHistoryItem[]> {
	const { data } = await http.delete(`/utility/system-config/broadcast-history/${historyId}`);
	return data?.data;
}
